import { Controller } from "@hotwired/stimulus";
import { Sortable } from "@shopify/draggable";
import { patch } from "@rails/request.js";

/**
 * Handles drag-and-drop sorting of menu sections in the navigation bar
 *
 * Requirements:
 * - Each sortable item must have data-section-navigation-sortable-target="item"
 * - Each item must have a data-section-id attribute
 * - Drag handles must have data-section-navigation-sortable-handle attribute
 * - Controller needs a sort-url-value pointing to the server endpoint
 */
export default class extends Controller {
  static targets = ["item"];
  static values = {
    sortUrl: String,
    rootElementSelector: String,
  };

  connect() {
    this.initializeSortable();
  }

  disconnect() {
    this.sortable?.destroy();
  }

  /**
   * Initialize Shopify's Draggable sortable instance
   * @private
   */
  initializeSortable() {
    this.sortable = new Sortable(this.element, {
      draggable: "[data-section-navigation-sortable-target='item']",
      handle: "[data-section-navigation-sortable-handle]",
      delay: 0,
      mirror: {
        constrainDimensions: true,
        // Append mirror to body to avoid positioning issues with CSS containment
        // The parent container uses "contain: layout" which creates a new containing block
        // for positioned elements, causing the draggable mirror to be positioned incorrectly
        appendTo: "body",
      },
    });

    this.sortable.on("sortable:stop", this.handleSortableStop.bind(this));
  }

  /**
   * Handles the end of a drag-and-drop operation
   * Gets the new section order and sends it to the server
   * @private
   */
  handleSortableStop(event) {
    // Read the dragged element from the event itself rather than scanning
    // targets for a transient class. The source clone is removed during
    // teardown and Turbo broadcasts can mutate the DOM mid-drag, so the
    // class-based search was racy and crashed when it returned undefined.
    const sectionThatWasSortedId =
      event?.dragEvent?.originalSource?.dataset?.sectionId;

    if (!sectionThatWasSortedId) return;

    const sectionElements = this.itemTargets.filter(
      (element) =>
        !element.classList.contains("draggable--original") &&
        !element.classList.contains("draggable-mirror"),
    );

    const sectionIds = sectionElements
      .map((element) => element.dataset.sectionId)
      .filter(Boolean);

    this.updateSectionOrder(sectionIds, sectionThatWasSortedId).catch(
      (error) => {
        console.error("Failed to persist section order", error);
      },
    );
  }

  /**
   * Sends the new section order to the server
   * @private
   * @param {string[]} sectionIds - Array of section IDs in their new order
   * @param {string} sectionThatWasSortedId - id of the dragged section
   */
  async updateSectionOrder(sectionIds, sectionThatWasSortedId) {
    await patch(this.sortUrlValue, {
      body: {
        menu_section_sort: {
          section_ids: sectionIds,
          section_that_was_sorted: sectionThatWasSortedId,
          root_element_selector: this.rootElementSelectorValue,
        },
      },
    });
  }

  get scrollableElement() {
    if (this.hasRootElementSelectorValue) {
      return document.querySelector(this.rootElementSelectorValue);
    } else {
      return document.querySelector("main");
    }
  }
}
